<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Modern Header -->
      <header class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z"/>
            </svg>
          </div>
          <div class="header-text">
            <h2>{{ $t('upload.title') }}</h2>
          </div>
        </div>
        <button @click="closeModal" class="close-btn" aria-label="Close modal">
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
          </svg>
        </button>
      </header>

      <!-- Alert Messages -->
      <div v-if="error" class="alert alert-error">
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"/>
        </svg>
        <span>{{ error }}</span>
      </div>

      <div v-if="success" class="alert alert-success">
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M17,9L15.59,7.58L10,13.17L8.41,11.59L7,13L10,16L17,9Z"/>
        </svg>
        <span>{{ success }}</span>
      </div>

      <form @submit.prevent="handleUpload" class="upload-form">
        <!-- File Upload Section -->
        <div class="form-section">
          <div class="form-group">

            <div class="file-upload-area" :class="{ 'drag-over': dragOver }">
              <input
                ref="fileInput"
                type="file"
                :accept="isMobile ? 'image/jpeg,image/jpg,image/png,image/webp,text/plain' : 'image/jpeg,image/jpg,image/png,image/webp'"
                multiple
                @change="handleFileSelect"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="handleFileDrop"
                class="file-input"
                :class="{ 'file-input-disabled': selectedFiles.length > 0 }"
              />
              <div class="file-upload-content">
                <div v-if="selectedFiles.length === 0" class="upload-placeholder">
                  <div class="upload-icon">
                    <svg width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                    </svg>
                  </div>
                  <h3>{{ isMobile ? $t('upload.dropzone.title') : $t('upload.dropzone.selectPhotos') }}</h3>
                  <p>{{ isMobile ? $t('upload.dropzone.multipleDescription') : $t('upload.dropzone.multipleDescription') }}</p>
                  <p class="upload-hint">{{ $t('upload.requirements.size') }}</p>
                </div>
                <div v-else class="files-preview">
                  <!-- Multiple files preview (for all devices) -->
                  <div class="multiple-files-preview">
                    <div class="files-header">
                      <h4>{{ $t('upload.files.selected', { count: selectedFiles.length }) }}</h4>
                      <div class="files-actions">
                        <button type="button" @click="clearFiles" class="btn btn-secondary btn-small">
                          {{ $t('upload.files.clearAll') }}
                        </button>
                      </div>
                    </div>
                    <div class="files-list">
                      <div v-for="(file, index) in selectedFiles" :key="index" class="file-item-with-tags">
                        <div class="file-preview-section">
                          <img v-if="previewUrls[index]" :src="previewUrls[index]" alt="Preview" class="file-thumbnail" />
                          <div class="file-details">
                            <span class="file-name">{{ file.name }}</span>
                            <span class="file-size">{{ formatFileSize(file.size) }}</span>
                          </div>
                        </div>
                        <div class="file-tags-section">
                          <input
                            v-model="fileTags[index]"
                            type="text"
                            :placeholder="$t('upload.files.tagsFor', { filename: file.name })"
                            class="file-tags-input"
                            @input="validateFileTags(index)"
                          />
                          <div v-if="fileTagErrors[index]" class="file-tag-error">
                            {{ fileTagErrors[index] }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="files-summary">
                      <span>{{ $t('upload.files.totalSize', { size: formatFileSize(totalFileSize) }) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Upload Progress -->
            <div v-if="uploading && uploadProgress.total > 0" class="upload-progress-section">
              <div class="progress-header">
                <h4>{{ $t('upload.progress.uploading', { total: uploadProgress.total }) }}</h4>
                <span>{{ $t('upload.progress.completed', { completed: uploadProgress.completed, total: uploadProgress.total }) }}</span>
              </div>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
              </div>
              <div class="progress-details">
                <span v-if="uploadProgress.current">{{ uploadProgress.current }}</span>
                <span>{{ progressPercentage }}%</span>
              </div>
              <div class="progress-note">
                <p><strong>Note:</strong> Large images (>10MB) may take up to 10 minutes to process and resize. Please be patient.</p>
              </div>
              <div v-if="uploadProgress.errors.length > 0" class="progress-errors">
                <h5>{{ $t('upload.progress.errors', { count: uploadProgress.errors.length }) }}</h5>
                <ul>
                  <li v-for="error in uploadProgress.errors" :key="error.file">
                    <strong>{{ error.file }}:</strong> {{ error.message }}
                  </li>
                </ul>
              </div>
            </div>
            
            <!-- GPS Validation Error -->
            <div v-if="selectedFiles.length > 0 && gpsValidationError" class="validation-error gps-error">
              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z"/>
              </svg>
              <span>{{ gpsValidationError }}</span>
            </div>
          </div>
        </div>

        <!-- Requirements Notice (full width below drag and drop) -->
        <div v-if="selectedFiles.length === 0" class="requirements-section">
          <div class="requirements-card-full">
            <div class="requirements-header">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
              <h4>{{ $t('upload.requirements.title') }}</h4>
            </div>
            <ul class="requirements-list-full">
              <li class="requirement-item">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M17,9L15.59,7.58L10,13.17L8.41,11.59L7,13L10,16L17,9Z"/>
                </svg>
                <span><strong>{{ $t('upload.requirements.format') }}</strong> ({{ $t('upload.requirements.size') }})</span>
              </li>
              <li v-if="isMobile" class="requirement-item">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                </svg>
                <span><strong>Mobile Note:</strong> File picker may show other file types, but only select JPEG images. Multiple selection is now supported!</span>
              </li>
              <li class="requirement-item">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z"/>
                </svg>
                <span><strong>{{ $t('upload.requirements.gps') }}</strong></span>
              </li>
              <li class="requirement-item">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5.5,7A1.5,1.5 0 0,1 4,5.5A1.5,1.5 0 0,1 5.5,4A1.5,1.5 0 0,1 7,5.5A1.5,1.5 0 0,1 5.5,7M21.41,11.58L12.41,2.58C12.05,2.22 11.55,2 11,2H4C2.89,2 2,2.89 2,4V11C2,11.55 2.22,12.05 2.59,12.41L11.58,21.41C11.95,21.78 12.45,22 13,22C13.55,22 14.05,21.78 14.41,21.41L21.41,14.41C21.78,14.05 22,13.55 22,13C22,12.45 21.78,11.95 21.41,11.58Z"/>
                </svg>
                <span><strong>{{ $t('upload.requirements.tags') }}</strong></span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Upload Button (centered below requirements) -->
        <div class="form-actions-centered">
          <button
            type="submit"
            class="btn btn-primary btn-large"
            :class="{ 'btn-disabled': !isFormValid || uploading }"
            :disabled="!isFormValid || uploading"
          >
            <svg v-if="uploading" width="20" height="20" fill="currentColor" viewBox="0 0 24 24" class="spinner">
              <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
            </svg>
            <svg v-else width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z"/>
            </svg>
            <span v-if="uploading">{{ $t('upload.form.uploading') }}</span>
            <span v-else-if="selectedFiles.length === 0">{{ $t('upload.validation.fileRequired') }}</span>
            <span v-else-if="gpsValidationError">{{ $t('upload.validation.gpsRequired') }}</span>
            <span v-else-if="!isFormValid">{{ $t('upload.files.tagsRequired') }}</span>
            <span v-else>{{ selectedFiles.length === 1 ? $t('upload.form.submit') : $t('upload.files.uploadButton', { count: selectedFiles.length }) }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { photosAPI } from '../services/api'

export default {
  name: 'UploadModal',
  data() {
    return {
      selectedFiles: [],
      previewUrls: [],
      fileTags: [], // Individual tags for each file
      fileTagErrors: [], // Validation errors for individual file tags
      uploading: false,
      error: '',
      success: '',
      dragOver: false,
      gpsValidationError: '',
      hasGpsData: null, // null = not checked, true = has GPS, false = no GPS

      uploadProgress: {
        total: 0,
        completed: 0,
        current: '',
        errors: []
      }
    }
  },
  computed: {
    isFormValid() {
      if (this.selectedFiles.length === 0) return false
      
      // Check individual tags for all files
      return this.selectedFiles.every((file, index) => {
        const fileTags = this.fileTags[index] || ''
        return fileTags.trim() && 
               fileTags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0).length > 0
      }) && !this.gpsValidationError && this.fileTagErrors.every(error => !error)
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },
    totalFileSize() {
      return this.selectedFiles.reduce((total, file) => total + file.size, 0)
    },
    progressPercentage() {
      if (this.uploadProgress.total === 0) return 0
      return Math.round((this.uploadProgress.completed / this.uploadProgress.total) * 100)
    }
  },
  methods: {
    closeModal() {
      this.$emit('close')
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.setSelectedFiles(files)
    },

    async handleFileDrop(event) {
      this.dragOver = false
      
      // Handle both files and folders
      const items = Array.from(event.dataTransfer.items)
      const files = []
      
      for (const item of items) {
        if (item.kind === 'file') {
          const entry = item.webkitGetAsEntry()
          if (entry) {
            if (entry.isDirectory) {
              // Handle folder
              const folderFiles = await this.readDirectory(entry)
              files.push(...folderFiles)
            } else {
              // Handle single file
              files.push(item.getAsFile())
            }
          } else {
            // Fallback for browsers that don't support webkitGetAsEntry
            files.push(item.getAsFile())
          }
        }
      }
      
      // If no files found through items, fallback to dataTransfer.files
      if (files.length === 0) {
        const fallbackFiles = Array.from(event.dataTransfer.files)
        files.push(...fallbackFiles)
      }
      
      console.log('Total files found after processing:', files.length)
      this.setSelectedFiles(files)
    },

    setSelectedFiles(files) {
      if (!files || files.length === 0) return

      console.log('Selected files:', files.length)

      // Clear previous selections
      this.clearFiles()

      const validFiles = []
      const errors = []

      // File size limit (60MB for all devices)
      const maxSize = 60 * 1024 * 1024

      for (const file of files) {
        // Skip null or undefined files
        if (!file) {
          continue
        }

        // Validate file type - silently skip non-JPEG files when many files are selected (likely from folder)
        if (!file.type || !file.type.match(/^image\/(jpeg|jpg)$/)) {
          // Only show error for individual file selection (few files), not bulk uploads
          if (files.length <= 5) {
            errors.push(`${file.name}: Only JPEG files are allowed`)
          }
          continue
        }

        // Validate file size
        if (file.size > maxSize) {
          const maxSizeMB = '60MB'
          errors.push(`${file.name}: File size must be less than ${maxSizeMB}`)
          continue
        }

        validFiles.push(file)
      }

      // Only show errors if we have some, and either few files selected or no valid files found
      if (errors.length > 0 && (files.length <= 5 || validFiles.length === 0)) {
        this.error = errors.join('; ')
        if (validFiles.length === 0) return
      } else {
        this.error = ''
      }

      this.selectedFiles = validFiles
      this.hasGpsData = null // Reset GPS validation state
      this.gpsValidationError = ''

      // Initialize individual file tags
      this.fileTags = new Array(validFiles.length).fill('')
      this.fileTagErrors = new Array(validFiles.length).fill('')

      // Create preview URLs
      this.createPreviewUrls()
    },

    createPreviewUrls() {
      this.previewUrls = []
      
      // Limit previews to first 10 files for performance
      const filesToPreview = this.selectedFiles.slice(0, 10)
      
      for (const file of filesToPreview) {
        try {
          const previewUrl = URL.createObjectURL(file)
          this.previewUrls.push(previewUrl)
        } catch (error) {
          console.error('Error creating preview URL:', error)
          this.previewUrls.push(null)
        }
      }
    },

    clearFiles() {
      this.selectedFiles = []
      this.hasGpsData = null
      this.gpsValidationError = ''
      
      // Clean up preview URLs
      for (const url of this.previewUrls) {
        if (url) {
          URL.revokeObjectURL(url)
        }
      }
      this.previewUrls = []
      
      // Reset individual file tags
      this.fileTags = []
      this.fileTagErrors = []
      
      // Reset upload progress
      this.uploadProgress = {
        total: 0,
        completed: 0,
        current: '',
        errors: []
      }
      
      this.$refs.fileInput.value = ''
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    async handleUpload() {
      if (this.selectedFiles.length === 0) {
        this.error = this.$t('upload.validation.fileRequired')
        return
      }

      // Validate individual tags for all files
      for (let i = 0; i < this.selectedFiles.length; i++) {
        const fileTags = this.fileTags[i] || ''
        if (!fileTags.trim()) {
          this.error = `Tags are required for ${this.selectedFiles[i].name}`
          return
        }
        const tagList = fileTags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        if (tagList.length === 0) {
          this.error = `At least one valid tag is required for ${this.selectedFiles[i].name}`
          return
        }
      }

      this.uploading = true
      this.error = ''
      this.success = ''

      // Initialize progress tracking
      this.uploadProgress = {
        total: this.selectedFiles.length,
        completed: 0,
        current: '',
        errors: []
      }

      console.log('Starting batch upload:', {
        fileCount: this.selectedFiles.length
      })

      const successfulUploads = []

      try {
        // Upload files one by one to track progress
        for (let i = 0; i < this.selectedFiles.length; i++) {
          const file = this.selectedFiles[i]
          this.uploadProgress.current = file.name

          try {
            const formData = new FormData()
            formData.append('photo', file)
            
            // Use individual tags for all files
            const tagsToUse = this.fileTags[i].trim()
            formData.append('tags', tagsToUse)

            const response = await photosAPI.upload(formData)
            successfulUploads.push(response.data)
            this.uploadProgress.completed++

          } catch (error) {
            console.error(`Upload error for ${file.name}:`, error)
            
            let errorMessage = error.response?.data?.message || 'Upload failed'
            
            // Handle specific error types
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
              errorMessage = 'Upload timeout - large images may take up to 10 minutes to process. Please check if the photo was saved successfully.'
            } else if (error.response?.data?.message?.includes('GPS') || 
                error.response?.data?.message?.includes('location data')) {
              if (error.response?.data?.message?.includes('GPS fields detected but no coordinates found')) {
                errorMessage = 'Location services were disabled when this photo was taken'
              } else {
                errorMessage = 'GPS location data is required'
              }
            } else if (error.response?.status === 413) {
              errorMessage = 'File too large'
            } else if (error.response?.status === 502) {
              errorMessage = 'Server temporarily unavailable'
            } else if (error.response?.status === 504) {
              errorMessage = 'Server timeout - image may still be processing. Please check if the photo was saved.'
            }

            this.uploadProgress.errors.push({
              file: file.name,
              message: errorMessage
            })
          }
        }

        // Show results
        if (successfulUploads.length === this.selectedFiles.length) {
          this.success = this.$t('upload.progress.successAll', { count: successfulUploads.length })
          
          // Reset form after successful upload
          this.clearFiles()
          this.gpsValidationError = ''
          this.hasGpsData = null

          // Emit success event after a short delay
          setTimeout(() => {
            this.$emit('upload-success')
          }, 2000)
        } else if (successfulUploads.length > 0) {
          this.success = this.$t('upload.progress.successPartial', { 
            successful: successfulUploads.length, 
            total: this.selectedFiles.length 
          })
        } else {
          this.error = this.$t('upload.progress.failed')
        }

      } catch (error) {
        console.error('Batch upload error:', error)
        this.error = 'Upload process failed. Please try again.'
      } finally {
        this.uploading = false
        this.uploadProgress.current = ''
      }
    },

    validateFileTags(index) {
      const tags = this.fileTags[index] || ''
      const tagList = tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
      
      if (tagList.length === 0) {
        this.fileTagErrors[index] = 'At least one tag is required'
      } else {
        this.fileTagErrors[index] = ''
      }
    },

    async readDirectory(directoryEntry) {
      const files = []
      
      return new Promise((resolve) => {
        const reader = directoryEntry.createReader()
        
        const readEntries = () => {
          reader.readEntries(async (entries) => {
            if (entries.length === 0) {
              // No more entries, resolve with collected files
              resolve(files)
              return
            }
            
            // Process each entry
            for (const entry of entries) {
              if (entry.isFile) {
                // Get the actual file
                const file = await new Promise((fileResolve) => {
                  entry.file(fileResolve)
                })
                files.push(file)
              } else if (entry.isDirectory) {
                // Recursively read subdirectories
                const subFiles = await this.readDirectory(entry)
                files.push(...subFiles)
              }
            }
            
            // Continue reading (directories might have more entries than fit in one read)
            readEntries()
          })
        }
        
        readEntries()
      })
    },

    openCamera() {
      // Create a hidden file input for camera capture
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*,text/plain' // Add text/plain to preserve GPS data on mobile
      input.multiple = true // Allow multiple selection
      input.capture = 'camera' // Force camera on mobile
      input.style.display = 'none'

      input.addEventListener('change', (event) => {
        const files = Array.from(event.target.files)
        if (files.length > 0) {
          // Validate files before setting them
          const validFiles = []
          const errors = []

          for (const file of files) {
            // Validate file type - only allow JPEG
            if (!file.type || !file.type.match(/^image\/(jpeg|jpg)$/)) {
              errors.push(`${file.name}: Only JPEG files are allowed`)
              continue
            }

            // Validate file size (60MB for desktop)
            const maxSize = 60 * 1024 * 1024
            if (file.size > maxSize) {
              errors.push(`${file.name}: File size must be less than 60MB`)
              continue
            }

            validFiles.push(file)
          }

          if (errors.length > 0 && validFiles.length === 0) {
            this.error = errors.join('; ')
            return
          }

          if (validFiles.length > 0) {
            this.setSelectedFiles(validFiles)
          }
        }
        
        // Clean up
        document.body.removeChild(input)
      })

      // Add to DOM and trigger click
      document.body.appendChild(input)
      input.click()
    }
  }
}
</script>

<style scoped>
/* Modern Upload Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1.5rem;
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.modal-content {
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  position: relative;
  transform: translateY(20px) scale(0.95);
  animation: slideUp 0.3s ease 0.1s forwards;
}

@keyframes slideUp {
  to {
    transform: translateY(0) scale(1);
  }
}

/* Modern Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%);
  border-radius: 24px 24px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.header-text h2 {
  margin: 0;
  color: #1f2937;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
}

.header-text p {
  margin: 0.25rem 0 0;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

.close-btn {
  background: rgba(107, 114, 128, 0.1);
  border: 1px solid rgba(107, 114, 128, 0.2);
  border-radius: 10px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.close-btn:hover {
  background: rgba(107, 114, 128, 0.2);
  transform: scale(1.05);
  color: #374151;
}

/* Alert Messages */
.alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  margin: 0 2rem 1rem;
  border-radius: 12px;
  font-weight: 500;
  font-size: 0.875rem;
  animation: slideUp 0.3s ease;
}

.alert-error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #dc2626;
}

.alert-success {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.2);
  color: #059669;
}

/* Form Styles */
.upload-form {
  padding: 2rem 2rem 2rem;
  display: flex;
  flex-direction: column;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-label svg {
  color: #6366f1;
}

.form-description {
  margin: -0.25rem 0 0.5rem 0;
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
  line-height: 1.4;
}

/* File Upload Area */
.file-upload-area {
  border: 2px dashed rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  padding: 2.5rem;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: rgba(99, 102, 241, 0.02);
  cursor: pointer;
}

.file-upload-area:hover {
  border-color: rgba(99, 102, 241, 0.5);
  background: rgba(99, 102, 241, 0.05);
  transform: translateY(-2px);
}

.file-upload-area.drag-over {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  transform: scale(1.02);
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.2);
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  cursor: pointer;
}

.file-input-disabled {
  pointer-events: none;
}

.upload-placeholder {
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.upload-icon {
  color: #6366f1;
  opacity: 0.7;
}

.upload-placeholder h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 600;
}

.upload-placeholder p {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.upload-hint {
  color: #9ca3af !important;
  font-size: 0.8rem !important;
}

/* File Preview */
.file-preview {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  text-align: left;
}

.preview-container {
  position: relative;
  flex-shrink: 0;
}

.preview-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 12px;
  border: 3px solid rgba(99, 102, 241, 0.2);
}

.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.preview-container:hover .preview-overlay {
  opacity: 1;
}

.file-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 1rem;
  margin: 0;
}

.file-size {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.file-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 8px;
  color: #059669;
  font-size: 0.8rem;
  font-weight: 500;
  width: fit-content;
}

/* Form Input */
.form-input {
  padding: 0.875rem 1rem;
  border: 1px solid rgba(209, 213, 219, 0.8);
  border-radius: 12px;
  font-size: 0.875rem;
  background: rgba(255, 255, 255, 0.7);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  background: white;
}

.form-input::placeholder {
  color: #9ca3af;
}

.input-error {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
}

.input-hint {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
}

/* Validation Error */
.validation-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ef4444;
  font-size: 0.8rem;
  font-weight: 500;
}

.gps-error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 10px;
  padding: 0.875rem;
  margin-top: 0.75rem;
}

/* Tag Count Info */
.tag-count-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 0.75rem;
  color: #6366f1;
  font-weight: 500;
  font-size: 0.8rem;
}

/* Buttons */
.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}

.btn-primary {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: 1px solid transparent;
  box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.3);
}

.btn-primary:hover:not(.btn-disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.4);
}

.btn-secondary {
  background: rgba(107, 114, 128, 0.15);
  color: #1f2937;
  border: 1px solid rgba(107, 114, 128, 0.4);
  font-weight: 600;
}

.btn-secondary:hover:not(.btn-disabled) {
  background: rgba(107, 114, 128, 0.25);
  color: #111827;
  border-color: rgba(107, 114, 128, 0.6);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.4);
}

.btn-ghost {
  background: rgba(255, 255, 255, 0.9);
  color: #374151;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.btn-ghost:hover {
  background: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-large {
  padding: 1rem 2rem;
  font-size: 1rem;
  min-height: 48px;
  width: 100%;
}

.btn-sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
}

.btn-disabled {
  opacity: 0.6;
  cursor: not-allowed !important;
  transform: none !important;
  box-shadow: none !important;
}

.btn svg.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Form Layout */
.form-layout {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 2rem;
  align-items: start;
}

.left-column {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.right-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-actions {
  margin-top: 1rem;
}

/* Requirements Card */
.requirements-card {
  background: rgba(99, 102, 241, 0.05);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
  padding: 1.5rem;
  position: sticky;
  top: 1rem;
}

/* Full Width Requirements Section */
.requirements-section {
  /* margin: 2rem 0; */
}

.requirements-card-full {
  background: rgba(99, 102, 241, 0.05);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
}

.requirements-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  justify-content: center;
}

.requirements-header svg {
  color: #6366f1;
}

.requirements-header h4 {
  margin: 0;
  color: #1f2937;
  font-size: 1.125rem;
  font-weight: 600;
}

.requirements-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.requirements-list-full {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  justify-items: center;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #374151;
  font-size: 0.875rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.1);
  transition: all 0.2s ease;
  width: 100%;
  max-width: 320px;
}

.requirement-item:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
}

.requirement-item svg {
  color: #22c55e;
  flex-shrink: 0;
}

/* Centered Form Actions */
.form-actions-centered {
  display: flex;
  justify-content: center;
  margin: 2rem 0 1rem;
}

.form-actions-centered .btn-large {
  width: auto;
  min-width: 200px;
  max-width: 400px;
}

/* Form Label Section */
.form-label-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

/* Upload Mode Toggle */
.upload-mode-toggle {
  flex-shrink: 0;
}

.mode-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
  font-weight: 500;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  white-space: nowrap;
}

.mode-toggle-btn:hover {
  background: rgba(99, 102, 241, 0.2);
  border-color: rgba(99, 102, 241, 0.5);
  transform: translateY(-1px);
}

/* Multiple Files Preview */
.multiple-files-preview {
  text-align: left;
}

.files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.files-header h4 {
  margin: 0;
  color: #1f2937;
  font-size: 1rem;
  font-weight: 600;
}

.files-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.files-actions .btn-small {
  width: 100%;
  justify-content: center;
}

.files-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 1rem;
}

.file-item-with-tags {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
}

.file-preview-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.file-thumbnail {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.file-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.file-details .file-name {
  font-weight: 600;
  color: #f9fafb;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-details .file-size {
  color: #d1d5db;
  font-size: 0.75rem;
}

.file-tags-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-tags-input {
  padding: 0.75rem;
  border: 1px solid rgba(209, 213, 219, 0.8);
  border-radius: 8px;
  font-size: 0.875rem;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
  color: #1f2937;
}

.file-tags-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  background: white;
}

.file-tags-input::placeholder {
  color: #9ca3af;
  font-size: 0.8rem;
}

.file-tag-error {
  color: #ef4444;
  font-size: 0.75rem;
  font-weight: 500;
  margin-top: 0.25rem;
}

.files-summary {
  padding: 0.75rem;
  background: rgba(99, 102, 241, 0.05);
  border-radius: 8px;
  text-align: center;
  color: #6366f1;
  font-weight: 500;
  font-size: 0.875rem;
}

/* Upload Progress */
.upload-progress-section {
  margin: 1.5rem 0;
  padding: 1.5rem;
  background: rgba(99, 102, 241, 0.05);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.progress-header h4 {
  margin: 0;
  color: #f9fafb;
  font-size: 1rem;
  font-weight: 600;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  color: #f9fafb;
  margin-bottom: 1rem;
}

.progress-note {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(99, 102, 241, 0.05);
  border-radius: 8px;
  text-align: center;
  color: #6366f1;
  font-weight: 500;
  font-size: 0.875rem;
}

.progress-errors {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
}

.progress-errors h5 {
  margin: 0 0 0.5rem 0;
  color: #dc2626;
  font-size: 0.875rem;
  font-weight: 600;
}

.progress-errors ul {
  margin: 0;
  padding-left: 1rem;
  list-style-type: disc;
}

.progress-errors li {
  color: #dc2626;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

/* Mobile Camera Section */
.mobile-camera-section {
  margin-top: 1rem;
  text-align: center;
}

.mobile-camera-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border: 2px solid #10b981;
  border-radius: 12px;
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.mobile-camera-btn:hover {
  background: rgba(16, 185, 129, 0.2);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.mobile-camera-btn:active {
  transform: translateY(0);
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 1rem;
  }

  .modal-content {
    max-height: 80vh;
    border-radius: 20px;
  }

  .modal-header {
    padding: 1.5rem 1.5rem 1rem;
    border-radius: 20px 20px 0 0;
  }

  .header-content {
    gap: 0.75rem;
  }

  .header-icon {
    width: 40px;
    height: 40px;
  }

  .header-text h2 {
    font-size: 1.5rem;
  }

  .upload-form {
    padding: 1.5rem 1.5rem 1.5rem;
  }

  .file-upload-area {
    padding: 2rem 1.5rem;
  }

  .file-preview {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .preview-image {
    width: 100px;
    height: 100px;
  }

  .requirements-section {
    margin: 1.5rem 0;
  }

  .requirements-card-full {
    padding: 1.5rem;
  }

  .requirements-list-full {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .requirement-item {
    max-width: none;
  }

  .form-actions-centered {
    margin: 1.5rem 0 1rem;
  }

  .form-actions-centered .btn-large {
    min-width: 180px;
    padding: 0.875rem 1.5rem;
  }

  /* Mobile Multiple Files Improvements */
  .files-list {
    max-height: 300px; /* Reduce height on mobile */
  }

  .file-item-with-tags {
    padding: 1rem 0.75rem; /* Reduce padding on mobile */
  }

  .file-thumbnail {
    width: 60px; /* Larger thumbnail on mobile */
    height: 60px;
  }

  .file-details .file-name {
    font-size: 0.875rem; /* Slightly larger text on mobile */
  }

  .file-tags-input {
    padding: 0.875rem; /* More padding for easier touch input */
    font-size: 0.875rem; /* Larger text for mobile */
  }

  .files-actions {
    flex-direction: column; /* Stack buttons vertically on mobile */
    gap: 0.5rem;
    align-items: stretch;
  }

  .files-actions .btn-small {
    width: 100%; /* Full width buttons on mobile */
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .modal-header {
    padding: 1rem;
  }

  .header-text h2 {
    font-size: 1.25rem;
  }

  .upload-form {
    padding: 1.5rem 1rem 1rem;
  }

  .file-upload-area {
    padding: 1.5rem 1rem;
  }

  .upload-placeholder h3 {
    font-size: 1.125rem;
  }

  .alert {
    margin: 1rem;
  }

  .requirements-section {
    margin: 1rem 0;
  }

  .requirements-card-full {
    padding: 1rem;
  }

  .requirements-header h4 {
    font-size: 1rem;
  }

  .requirement-item {
    padding: 0.75rem;
    font-size: 0.8rem;
  }

  .form-actions-centered .btn-large {
    min-width: 160px;
    font-size: 0.875rem;
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .modal-content {
    background: rgba(17, 24, 39, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .modal-header {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%);
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }

  .header-text h2 {
    color: #f9fafb;
  }

  .header-text p {
    color: #9ca3af;
  }

  .close-btn {
    background: rgba(55, 65, 81, 0.3);
    border-color: rgba(255, 255, 255, 0.2);
    color: #9ca3af;
  }

  .close-btn:hover {
    background: rgba(55, 65, 81, 0.5);
    color: #f3f4f6;
  }

  .form-label {
    color: #f3f4f6;
  }

  .form-description {
    color: #9ca3af;
  }

  .form-input {
    background: rgba(17, 24, 39, 0.8);
    border-color: rgba(255, 255, 255, 0.2);
    color: #f9fafb;
  }

  .form-input:focus {
    background: rgba(17, 24, 39, 0.95);
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
  }

  .form-input::placeholder {
    color: #6b7280;
  }

  .file-upload-area {
    border-color: rgba(99, 102, 241, 0.4);
    background: rgba(99, 102, 241, 0.05);
  }

  .file-upload-area:hover {
    background: rgba(99, 102, 241, 0.1);
  }

  .upload-placeholder h3 {
    color: #f9fafb;
  }

  .upload-placeholder p {
    color: #d1d5db;
  }

  .file-name {
    color: #f9fafb;
  }

  .file-size {
    color: #9ca3af;
  }

  .input-hint {
    color: #9ca3af;
  }

  .requirements-card {
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.2);
  }

  .requirements-header h4 {
    color: #f9fafb;
  }

  .requirement-item {
    color: #e5e7eb;
    background: rgba(55, 65, 81, 0.3);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .requirement-item:hover {
    background: rgba(55, 65, 81, 0.5);
  }

  .btn-ghost {
    background: rgba(55, 65, 81, 0.3);
    color: #f3f4f6;
    border-color: rgba(255, 255, 255, 0.2);
  }

  .btn-ghost:hover {
    background: rgba(55, 65, 81, 0.5);
    box-shadow: 0 4px 12px rgba(55, 65, 81, 0.4);
  }

  .form-description {
    color: #9ca3af;
  }

  .files-header h4 {
    color: #f9fafb;
  }

  .file-details .file-name {
    color: #f9fafb;
  }

  .file-details .file-size {
    color: #d1d5db;
  }

  .file-item-with-tags {
    background: rgba(55, 65, 81, 0.3);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .file-tags-input {
    background: rgba(17, 24, 39, 0.8);
    border-color: rgba(255, 255, 255, 0.2);
    color: #f9fafb;
  }

  .file-tags-input:focus {
    background: rgba(17, 24, 39, 0.95);
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
  }

  .file-tags-input::placeholder {
    color: #6b7280;
  }

  .progress-header h4 {
    color: #f9fafb;
  }

  .progress-details {
    color: #d1d5db;
  }

  .upload-placeholder h3 {
    color: #f9fafb;
  }

  .upload-placeholder p {
    color: #d1d5db;
  }

  .upload-hint {
    color: #9ca3af;
  }
}
</style> 